import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Calendar, 
  User, 
  Monitor, 
  Globe,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  AlertCircle,
  Activity,
  Loader2,
  Mail,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Auditoria = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    operation: 'all',
    search: '',
    date_from: '',
    date_to: ''
  });
  
  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const searchTimerRef = React.useRef(null);

  const loadOperations = useCallback(async () => {
    try {
      const response = await api.get('/audit/operations');
      setOperations(response.data || []);
    } catch (error) {
      console.error('Error loading operations:', error);
    }
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.pageSize);
      
      if (filters.operation && filters.operation !== 'all') params.append('operation', filters.operation);
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/audit/logs?${params.toString()}`);
      
      setLogs(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        page: response.data.page,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Error al cargar los registros de auditoría');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  useEffect(() => {
    loadLogs(1);
  }, [filters]);

  const handleSearch = () => {
    loadLogs(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setFilters({
      operation: 'all',
      search: '',
      date_from: '',
      date_to: ''
    });
  };
  
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearching(true);
    searchTimerRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value }));
      setSearching(false);
    }, 400);
  };

  const handleViewDetail = async (log) => {
    try {
      const response = await api.get(`/audit/logs/${log.id}`);
      setSelectedLog(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar el detalle');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEntityTypeBadge = (type) => {
    const colors = {
      requerimiento: 'bg-blue-100 text-blue-800',
      inspeccion: 'bg-purple-100 text-purple-800',
      atencion: 'bg-green-100 text-green-800',
      acta_diaria: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      requerimiento: 'Requerimiento',
      inspeccion: 'Inspección',
      atencion: 'Atención',
      acta_diaria: 'Acta Diaria'
    };
    return (
      <Badge className={`${colors[type] || 'bg-gray-100 text-gray-800'} font-normal text-xs px-2.5 py-0.5`}>
        {labels[type] || type}
      </Badge>
    );
  };

  const getOperationBadge = (operation) => {
    const isUpdate = operation?.startsWith('UPDATE');
    const isFinish = operation?.startsWith('FINISH');
    
    if (isFinish) {
      return <Badge className="bg-emerald-100 text-emerald-800 font-normal text-xs px-2.5 py-0.5">Finalización</Badge>;
    }
    if (isUpdate) {
      return <Badge className="bg-amber-100 text-amber-800 font-normal text-xs px-2.5 py-0.5">Actualización</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800 font-normal text-xs px-2.5 py-0.5 max-w-[130px] truncate" title={operation}>{operation}</Badge>;
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="auditoria-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D]">
            Auditoría del Sistema
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            Registro de transacciones y cambios importantes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={16} />
            <span className="hidden sm:inline">Filtros</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadLogs(pagination.page)}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-[#E8E8E7]">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Operación</Label>
                <Select
                  value={filters.operation}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, operation: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {operations.map(op => (
                      <SelectItem key={op.code} value={op.code}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Buscar</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#888888]" />
                  <Input
                    placeholder="Usuario, entidad, código, resumen..."
                    value={searchInput}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    className="h-9 pl-8 pr-8"
                    data-testid="audit-search-input"
                  />
                  {searching && (
                    <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9d8272] animate-spin" />
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Desde</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                  className="h-9"
                />
              </div>

              <div>
                <Label className="text-xs text-[#5A5A5A] mb-1.5 block">Hasta</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                  className="h-9"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
              >
                Limpiar
              </Button>
              <Button
                size="sm"
                onClick={handleSearch}
                className="bg-[#9d8272] hover:bg-[#8a7264]"
              >
                <Search size={16} className="mr-2" />
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-[#E8E8E7]">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Activity size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-[#888888]">Total Registros</p>
                <p className="text-lg font-semibold text-[#2D2D2D]">{pagination.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw size={32} className="animate-spin text-[#9d8272] mx-auto mb-3" />
                <p className="text-sm text-[#888888]">Cargando registros...</p>
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={48} className="text-[#E8E8E7] mb-3" />
              <p className="text-[#888888]">No se encontraron registros de auditoría</p>
              <p className="text-sm text-[#AAAAAA] mt-1">
                Los registros aparecerán cuando se realicen transacciones auditables
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '16%'}}>Fecha/Hora</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '20%'}}>Usuario</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '16%'}}>Operación</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '12%'}}>Entidad</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '9%'}}>Código</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '21%'}}>Resumen</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-[#2D2D2D]" style={{width: '6%'}}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-100 hover:bg-[#FAFAFA]">
                        <td className="py-1.5 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                            <Clock size={14} className="text-[#AAAAAA] flex-shrink-0" />
                            {formatDate(log.timestamp)}
                          </div>
                        </td>
                        <td className="py-1.5 px-4">
                          <p className="text-sm font-semibold text-[#2D2D2D]">{log.user_nombre || '-'}</p>
                          <p className="text-xs text-[#999999] mt-0.5">{log.user_email}</p>
                        </td>
                        <td className="py-1.5 px-4">
                          <div>
                            {getOperationBadge(log.operation)}
                            <p className="text-xs text-[#999999] mt-1 truncate" title={log.operation_label}>{log.operation_label}</p>
                          </div>
                        </td>
                        <td className="py-1.5 px-4 whitespace-nowrap">
                          {getEntityTypeBadge(log.entity_type)}
                        </td>
                        <td className="py-1.5 px-4">
                          <span className="text-sm text-[#5A5A5A] truncate block" style={{maxWidth: '80px'}} title={log.entity_code}>
                            {log.entity_code || '-'}
                          </span>
                        </td>
                        <td className="py-1.5 px-4" style={{maxWidth: '1px'}}>
                          <p className="text-sm text-[#5A5A5A] truncate" title={log.changes_summary}>
                            {log.changes_summary || '-'}
                          </p>
                        </td>
                        <td className="py-1.5 px-4 text-center">
                          <button
                            onClick={() => handleViewDetail(log)}
                            className="text-[#AAAAAA] hover:text-[#9d8272] transition-colors"
                          >
                            <Eye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-[#E8E8E7]">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-4 hover:bg-[#F9F9F9] cursor-pointer"
                    onClick={() => handleViewDetail(log)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {getEntityTypeBadge(log.entity_type)}
                          {getOperationBadge(log.operation)}
                        </div>
                        <p className="text-sm font-medium text-[#2D2D2D]">
                          {log.operation_label}
                        </p>
                        <p className="text-xs text-[#888888] mt-1">
                          {log.user_nombre} • {log.entity_code}
                        </p>
                        <p className="text-xs text-[#AAAAAA] mt-1 flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(log.timestamp)}
                        </p>
                      </div>
                      <Eye size={18} className="text-[#888888] flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-[#E8E8E7]">
              <p className="text-sm text-[#888888]">
                Mostrando {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} de {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-[#5A5A5A]">
                  {pagination.page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadLogs(pagination.page + 1)}
                  disabled={pagination.page >= totalPages || loading}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-[#9d8272]" />
              Detalle de Auditoría
            </DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-6">
              {/* General Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#888888]">Fecha y Hora</Label>
                  <p className="text-sm font-medium">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Operación</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {getOperationBadge(selectedLog.operation)}
                    <span className="text-sm">{selectedLog.operation_label}</span>
                  </div>
                </div>
              </div>

              {/* User Info */}
              <Card className="border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User size={16} className="text-[#9d8272]" />
                    Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#888888]">Nombre:</span>
                    <p className="font-medium">{selectedLog.user_nombre || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">Email o Teléfono:</span>
                    <p className="font-medium">{selectedLog.user_email}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Entity Info */}
              <Card className="border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText size={16} className="text-[#9d8272]" />
                    Entidad Afectada
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#888888]">Tipo:</span>
                    <div className="mt-1">{getEntityTypeBadge(selectedLog.entity_type)}</div>
                  </div>
                  <div>
                    <span className="text-[#888888]">Código:</span>
                    <p className="font-mono font-medium text-[#9d8272]">{selectedLog.entity_code || '-'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Changes Summary */}
              {selectedLog.changes_summary && (
                <Card className="border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Resumen de Cambios</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#5A5A5A] whitespace-pre-wrap">
                      {selectedLog.changes_summary}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Correo Enviado — solo para operaciones ENVIO_CORREO_* */}
              {selectedLog.new_values?.email_audit_id && (
                <Card className="border-blue-200 bg-blue-50/30" data-testid="email-audit-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Mail size={16} className="text-blue-600" />
                      Correo Enviado
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[#888888]">Destinatario:</span>
                        <p className="font-medium">{selectedLog.new_values.recipient_email || '-'}</p>
                      </div>
                      <div>
                        <span className="text-[#888888]">Estado:</span>
                        <p className="font-medium">
                          <Badge className="bg-green-100 text-green-700 font-normal">Enviado</Badge>
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                      {selectedLog.new_values.resend_email_id && (
                        <a
                          href={`https://resend.com/emails/${selectedLog.new_values.resend_email_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 bg-white border border-blue-200 rounded-md px-3 py-1.5 hover:bg-blue-50 transition-colors"
                          data-testid="resend-link"
                        >
                          <ExternalLink size={14} />
                          Ver en Resend
                        </a>
                      )}
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/email-audit/${selectedLog.new_values.email_audit_id}/preview`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[#9d8272] hover:text-[#7a6359] bg-white border border-[#d4c5bb] rounded-md px-3 py-1.5 hover:bg-[#f9f5f2] transition-colors"
                        data-testid="preview-link"
                      >
                        <Eye size={14} />
                        Ver correo enviado
                      </a>
                    </div>
                    <p className="text-xs text-[#AAAAAA] mt-1">
                      El log de Resend expira a los 30 d&#237;as. El link &ldquo;Ver correo enviado&rdquo; es permanente.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Old/New Values */}
              {(selectedLog.old_values || selectedLog.new_values) && (
                <Card className="border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Valores Modificados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedLog.old_values && (
                        <div>
                          <Label className="text-xs text-red-600 mb-2 block">Valores Anteriores</Label>
                          <pre className="text-xs bg-red-50 p-3 rounded-md overflow-auto max-h-40">
                            {JSON.stringify(selectedLog.old_values, null, 2)}
                          </pre>
                        </div>
                      )}
                      {selectedLog.new_values && (
                        <div>
                          <Label className="text-xs text-green-600 mb-2 block">Valores Nuevos</Label>
                          <pre className="text-xs bg-green-50 p-3 rounded-md overflow-auto max-h-40">
                            {JSON.stringify(selectedLog.new_values, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Device Info */}
              <Card className="border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Monitor size={16} className="text-[#9d8272]" />
                    Información del Dispositivo
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-[#888888]">Dispositivo:</span>
                    <p className="font-medium capitalize">{selectedLog.device_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">Navegador:</span>
                    <p className="font-medium">{selectedLog.browser || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">Sistema:</span>
                    <p className="font-medium">{selectedLog.os || '-'}</p>
                  </div>
                  <div>
                    <span className="text-[#888888]">IP:</span>
                    <p className="font-mono text-xs">{selectedLog.ip_address || '-'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auditoria;
